import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Table, Button, Badge, Alert, Modal, Form, InputGroup } from 'react-bootstrap';
import axios from 'axios';

interface ReferralStats {
  totalReferrals: number;
  pendingReferrals: number;
  completedReferrals: number;
  rewardedReferrals: number;
  totalReferrerRewards: number;
  totalRefereeRewards: number;
}

interface Referral {
  _id: string;
  referredUser: {
    _id: string;
    name: string;
    email: string;
    createdAt: string;
    userType: string;
  };
  referralCode: string;
  status: 'pending' | 'completed' | 'rewarded' | 'cancelled';
  referredAt: string;
  completedAt?: string;
  rewardedAt?: string;
  rewards: {
    referrerReward: number;
    refereeReward: number;
  };
}

const ReferralsPage: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalReferrals, setTotalReferrals] = useState(0);
    const referralsPerPage = 10;
  const [stats, setStats] = useState<ReferralStats>({
    totalReferrals: 0,
    pendingReferrals: 0,
    completedReferrals: 0,
    rewardedReferrals: 0,
    totalReferrerRewards: 0,
    totalRefereeRewards: 0
  });
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'rewarded'>('all');
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const loadReferralData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [statsResponse, historyResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/referrals/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/referrals/history`, {
          params: {
            status: statusFilter !== 'all' ? statusFilter : undefined,
            page: currentPage,
            limit: referralsPerPage
          },
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats);
        setReferralCode(statsResponse.data.referralCode);
      }

      if (historyResponse.data.success) {
        setReferrals(historyResponse.data.referrals);
        setTotalPages(historyResponse.data.pagination?.totalPages || 1);
        setTotalReferrals(historyResponse.data.pagination?.totalReferrals || historyResponse.data.total || 0);
      }

    } catch (error: any) {
      console.error('Error loading referral data:', error);
      setError(error.response?.data?.message || 'Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, API_BASE_URL]);

  useEffect(() => {
    loadReferralData();
  }, [loadReferralData, currentPage, statusFilter]);

  const copyReferralLink = async () => {
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = referralLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 3000);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      pending: 'warning',
      completed: 'info',
      rewarded: 'success',
      cancelled: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const getUserTypeBadge = (userType: string) => {
    const variants: { [key: string]: string } = {
      free: 'secondary',
      executive: 'warning',
      admin: 'danger'
    };
    const type = userType || 'free';
    return <Badge bg={variants[type] || 'secondary'}>{type.toUpperCase()}</Badge>;
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: 'fab fa-whatsapp',
      color: '#25D366',
      url: `https://wa.me/?text=Join NetworqPay and earn rewards! Use my referral code: ${referralCode} - ${window.location.origin}/register?ref=${referralCode}`
    },
    {
      name: 'Facebook',
      icon: 'fab fa-facebook-f',
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/register?ref=' + referralCode)}`
    },
    {
      name: 'Twitter',
      icon: 'fab fa-twitter',
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=Join NetworqPay and earn rewards!&url=${encodeURIComponent(window.location.origin + '/register?ref=' + referralCode)}&hashtags=networqpay,rewards,referral`
    },
    {
      name: 'LinkedIn',
      icon: 'fab fa-linkedin-in',
      color: '#0A66C2',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin + '/register?ref=' + referralCode)}`
    }
  ];

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">
              <i className="fas fa-users me-2"></i>
              Referral Program
            </h5>
          </Col>
          <Col xs="auto">
            <Button
              variant="outline-primary"
              onClick={loadReferralData}
              disabled={loading}
              className="me-2"
            >
              <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-2`}></i>
              Refresh
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowShareModal(true)}
              className="me-2"
            >
              <i className="fas fa-share-alt me-2"></i>
              Share & Earn
            </Button>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              style={{ width: 'auto', display: 'inline-block' }}
            >
              <option value="all">All Referrals</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="rewarded">Rewarded</option>
            </Form.Select>
          </Col>
        </Row>
      </div>

      <div className="admin-card-body">
        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <i className="fas fa-spinner fa-spin fa-2x mb-3"></i>
            <p>Loading referral data...</p>
          </div>
        ) : (
          <>
            {/* Your Referral Code Section */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={8}>
                    <h6 className="mb-3">
                      <i className="fas fa-code me-2 text-primary"></i>
                      Your Referral Code
                    </h6>
                    <InputGroup className="mb-3">
                      <Form.Control
                        type="text"
                        value={referralCode}
                        readOnly
                        className="bg-light"
                        style={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 'bold' }}
                      />
                      <Button
                        variant="outline-primary"
                        onClick={copyReferralLink}
                      >
                        <i className={`fas ${copiedToClipboard ? 'fa-check text-success' : 'fa-copy'} me-2`}></i>
                        {copiedToClipboard ? 'Copied!' : 'Copy Link'}
                      </Button>
                    </InputGroup>
                    <small className="text-muted">
                      Share this code with friends to earn rewards when they join and complete tasks!
                    </small>
                  </Col>
                  <Col md={4} className="text-center">
                    <div className="text-success mb-2">
                      <i className="fas fa-gift fa-3x"></i>
                    </div>
                    <h4 className="text-success mb-0">₹{stats.totalReferrerRewards}</h4>
                    <small className="text-muted">Total Earned</small>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Statistics Cards */}
            <Row className="mb-4">
              <Col md={3}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="text-primary mb-2">
                      <i className="fas fa-users fa-2x"></i>
                    </div>
                    <h4 className="mb-0">{stats.totalReferrals}</h4>
                    <small className="text-muted">Total Referrals</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="text-warning mb-2">
                      <i className="fas fa-clock fa-2x"></i>
                    </div>
                    <h4 className="mb-0">{stats.pendingReferrals}</h4>
                    <small className="text-muted">Pending</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="text-info mb-2">
                      <i className="fas fa-check-circle fa-2x"></i>
                    </div>
                    <h4 className="mb-0">{stats.completedReferrals}</h4>
                    <small className="text-muted">Completed</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="text-success mb-2">
                      <i className="fas fa-trophy fa-2x"></i>
                    </div>
                    <h4 className="mb-0">{stats.rewardedReferrals}</h4>
                    <small className="text-muted">Rewarded</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Referrals Table */}
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Referred User</th>
                    <th>User Type</th>
                    <th>Status</th>
                    <th>Joined Date</th>
                    <th>Your Reward</th>
                    <th>Their Reward</th>
                    <th>Completed Date</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                        <p className="text-muted mb-0">
                          {statusFilter === 'all' 
                            ? 'No referrals yet. Start sharing your referral code!'
                            : `No ${statusFilter} referrals found.`
                          }
                        </p>
                      </td>
                    </tr>
                  ) : (
                    referrals.map(referral => (
                      <tr key={referral._id}>
                        <td>
                          <div>
                            <strong>{referral.referredUser.name}</strong>
                            <br />
                            <small className="text-muted">{referral.referredUser.email}</small>
                          </div>
                        </td>
                        <td>
                          {getUserTypeBadge(referral.referredUser.userType)}
                        </td>
                        <td>
                          {getStatusBadge(referral.status)}
                        </td>
                        <td>
                          <small>{new Date(referral.referredUser.createdAt).toLocaleDateString()}</small>
                        </td>
                        <td>
                          <strong className="text-success">
                            ₹{referral.rewards.referrerReward || 0}
                          </strong>
                        </td>
                        <td>
                          <span className="text-info">
                            ₹{referral.rewards.refereeReward || 0}
                          </span>
                        </td>
                        <td>
                          {(referral.status === 'completed' || referral.status === 'rewarded') && referral.completedAt ? (
                            <small>{new Date(referral.completedAt).toLocaleDateString()}</small>
                          ) : referral.status === 'completed' || referral.status === 'rewarded' ? (
                            <small>{new Date(referral.referredUser.createdAt).toLocaleDateString()}</small>
                          ) : (
                            <small className="text-muted">Pending</small>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <small className="text-muted">
                      Showing {((currentPage - 1) * referralsPerPage) + 1} to {Math.min(currentPage * referralsPerPage, totalReferrals)} of {totalReferrals} referrals
                    </small>
                  </div>
                  <div className="btn-group">
                    <Button
                      variant="outline-primary"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline-primary"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button variant="primary" disabled>
                      {currentPage}
                    </Button>
                    <Button
                      variant="outline-primary"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline-primary"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Share Modal */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-share-alt me-2"></i>
            Share Your Referral Code
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="mb-3">Your Referral Link:</h6>
            <InputGroup>
              <Form.Control
                type="text"
                value={`${window.location.origin}/register?ref=${referralCode}`}
                readOnly
                className="bg-white"
                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
              />
              <Button variant="primary" onClick={copyReferralLink}>
                <i className={`fas ${copiedToClipboard ? 'fa-check' : 'fa-copy'} me-2`}></i>
                {copiedToClipboard ? 'Copied!' : 'Copy'}
              </Button>
            </InputGroup>
          </div>

          <h6 className="mb-3">Share on Social Media:</h6>
          <Row>
            {shareOptions.map(option => (
              <Col md={6} key={option.name} className="mb-3">
                <Button
                  variant="outline-secondary"
                  className="w-100 d-flex align-items-center justify-content-center"
                  onClick={() => window.open(option.url, '_blank')}
                  style={{ borderColor: option.color }}
                >
                  <i className={`${option.icon} me-2`} style={{ color: option.color }}></i>
                  Share on {option.name}
                </Button>
              </Col>
            ))}
          </Row>

          <div className="mt-4 p-3 border rounded">
            <h6 className="mb-2">
              <i className="fas fa-info-circle text-info me-2"></i>
              How Referrals Work
            </h6>
            <ul className="mb-0 small text-muted">
              <li>Share your referral code or link with friends</li>
              <li>When they sign up using your code, you both get signup bonuses</li>
              <li>Earn additional rewards when your referrals complete their first tasks</li>
              <li>Track all your referrals and earnings in this dashboard</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowShareModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReferralsPage;